/**
 * Wspólna warstwa wykresów (ECharts).
 *
 * Reguły, których trzymają się wszystkie wykresy w HQ:
 *
 * • **Jedna oś Y.** Nigdy dwie skale na jednym wykresie — dwie miary o różnym
 *   rzędzie wielkości to dwa wykresy albo indeks do wspólnej bazy. Podwójna oś
 *   pozwala „pokazać korelację", która nie istnieje.
 * • **Kolor idzie za bytem, nie za pozycją w rankingu.** Filtr zmieniający
 *   liczbę serii nie przemalowuje tych, które zostały.
 * • **Cienkie znaki, wycofana siatka.** Linia 2 px, punkt 8 px, siatka
 *   ledwo widoczna — dane mają być ciemniejsze niż tło, ale jaśniejsze niż
 *   dekoracja.
 * • **Legenda przy dwóch i więcej seriach.** Przy jednej serii tytuł wykresu
 *   już mówi, co to jest.
 *
 * Paleta serii pochodzi z `design/tokens.css` i przeszła walidator kontrastu
 * oraz rozróżnialności przy daltonizmie na powierzchni `--loam`.
 */

import * as echarts from "echarts/core";
import { BarChart, LineChart } from "echarts/charts";
import {
  DatasetComponent,
  GridComponent,
  LegendComponent,
  MarkLineComponent,
  TooltipComponent,
} from "echarts/components";
import { CanvasRenderer } from "echarts/renderers";
import type { EChartsOption } from "echarts";

echarts.use([
  BarChart,
  LineChart,
  DatasetComponent,
  GridComponent,
  LegendComponent,
  MarkLineComponent,
  TooltipComponent,
  CanvasRenderer,
]);

export { echarts };

const css = (name: string) =>
  getComputedStyle(document.documentElement).getPropertyValue(name).trim();

export function palette() {
  return {
    surface: css("--loam"),
    ink: css("--linen"),
    inkSoft: css("--flax"),
    inkMuted: css("--ash"),
    hairline: css("--hairline"),
    hairlineStrong: css("--hairline-strong"),
    series: [1, 2, 3, 4, 5, 6].map((index) => css(`--series-${index}`)),
    gain: [css("--gain-1"), css("--gain-2"), css("--gain-3")],
    loss: [css("--loss-1"), css("--loss-2")],
    accent: css("--straw"),
    mono: css("--font-mono"),
    display: css("--font-display"),
  };
}

type BaseOptions = {
  categories: string[];
  valueFormatter: (value: number) => string;
  legend?: boolean;
};

/** Szkielet wspólny dla wszystkich wykresów: siatka, osie, podpowiedź. */
function base({ categories, valueFormatter, legend }: BaseOptions): EChartsOption {
  const p = palette();
  return {
    animationDuration: 420,
    backgroundColor: "transparent",
    textStyle: { fontFamily: p.display, color: p.inkSoft },
    grid: {
      left: 8,
      right: 12,
      top: legend ? 44 : 16,
      bottom: 8,
      containLabel: true,
    },
    legend: legend
      ? {
          top: 0,
          left: 0,
          itemWidth: 10,
          itemHeight: 10,
          itemGap: 18,
          icon: "roundRect",
          textStyle: { color: p.inkSoft, fontSize: 12 },
        }
      : undefined,
    tooltip: {
      trigger: "axis",
      axisPointer: {
        type: "line",
        lineStyle: { color: p.hairlineStrong, width: 1 },
        // Krzyżyk zamiast cienia: cień pod słupkiem zasłania sam słupek.
      },
      backgroundColor: css("--soil"),
      borderColor: css("--hairline-strong"),
      borderWidth: 1,
      padding: [10, 12],
      textStyle: { color: p.ink, fontSize: 12, fontFamily: p.display },
      valueFormatter: (value) => valueFormatter(Number(value ?? 0)),
    },
    xAxis: {
      type: "category",
      data: categories,
      boundaryGap: false,
      axisLine: { lineStyle: { color: p.hairline } },
      axisTick: { show: false },
      axisLabel: {
        color: p.inkMuted,
        fontSize: 11,
        fontFamily: p.mono,
        hideOverlap: true,
      },
    },
    yAxis: {
      type: "value",
      splitLine: { lineStyle: { color: p.hairline, type: "solid" } },
      axisLabel: {
        color: p.inkMuted,
        fontSize: 11,
        fontFamily: p.mono,
        formatter: (value: number) => valueFormatter(value),
      },
    },
  };
}

export type SeriesSpec = {
  name: string;
  values: number[];
  color: string;
  area?: boolean;
  /** Linia przerywana — dla serii zbiorczej, która nie jest osobnym bytem. */
  dashed?: boolean;
  /** Punkty na linii — przy kilku kubełkach (miesiące) punkt jest daną. */
  symbols?: boolean;
};

/** Szereg czasowy: linia 2 px, opcjonalne bardzo delikatne wypełnienie. */
export function lineOption(
  categories: string[],
  series: SeriesSpec[],
  valueFormatter: (value: number) => string,
): EChartsOption {
  const option = base({ categories, valueFormatter, legend: series.length > 1 });
  return {
    ...option,
    series: series.map((spec) => ({
      type: "line",
      name: spec.name,
      data: spec.values,
      smooth: 0.18,
      showSymbol: spec.symbols ?? false,
      symbolSize: 8,
      lineStyle: { width: 2, color: spec.color, type: spec.dashed ? "dashed" : "solid" },
      itemStyle: { color: spec.color },
      emphasis: { focus: "series", scale: false },
      areaStyle: spec.area
        ? {
            color: {
              type: "linear",
              x: 0,
              y: 0,
              x2: 0,
              y2: 1,
              colorStops: [
                { offset: 0, color: `${spec.color}26` },
                { offset: 1, color: `${spec.color}00` },
              ],
            },
          }
        : undefined,
    })),
  };
}

/** Słupki dobowe — np. rejestracje. Zaokrąglony koniec przy wartości. */
export function barOption(
  categories: string[],
  series: SeriesSpec[],
  valueFormatter: (value: number) => string,
): EChartsOption {
  const option = base({ categories, valueFormatter, legend: series.length > 1 });
  return {
    ...option,
    xAxis: { ...(option.xAxis as object), boundaryGap: true },
    series: series.map((spec) => ({
      type: "bar",
      name: spec.name,
      data: spec.values,
      barMaxWidth: 14,
      itemStyle: { color: spec.color, borderRadius: [4, 4, 0, 0] },
      emphasis: { focus: "series" },
    })),
  };
}

/**
 * Most MRR: skąd wzięła się różnica między początkiem a końcem okresu.
 *
 * To nie jest pięć kategorii, tylko biegunowość — przyrosty jedną rampą
 * błękitu, ubytki jedną rampą różu. Kolor niesie znak, a nie tożsamość.
 */
export function mrrBridgeOption(
  movements: {
    start: number;
    new: number;
    expansion: number;
    reactivation: number;
    contraction: number;
    churn: number;
    end: number;
  },
  valueFormatter: (value: number) => string,
): EChartsOption {
  const p = palette();
  const steps = [
    { label: "Początek", value: movements.start, kind: "total" as const },
    { label: "Nowi", value: movements.new, kind: "gain" as const, tone: 0 },
    { label: "Rozwój", value: movements.expansion, kind: "gain" as const, tone: 1 },
    { label: "Powroty", value: movements.reactivation, kind: "gain" as const, tone: 2 },
    { label: "Obniżki", value: -movements.contraction, kind: "loss" as const, tone: 0 },
    { label: "Utraty", value: -movements.churn, kind: "loss" as const, tone: 1 },
    { label: "Koniec", value: movements.end, kind: "total" as const },
  ];

  const bases: number[] = [];
  const visible: number[] = [];
  let running = 0;
  for (const step of steps) {
    if (step.kind === "total") {
      bases.push(0);
      visible.push(step.value);
      running = step.value;
    } else {
      const next = running + step.value;
      bases.push(Math.min(running, next));
      visible.push(Math.abs(step.value));
      running = next;
    }
  }

  return {
    animationDuration: 420,
    backgroundColor: "transparent",
    textStyle: { fontFamily: p.display },
    grid: { left: 8, right: 12, top: 24, bottom: 8, containLabel: true },
    tooltip: {
      trigger: "item",
      backgroundColor: css("--soil"),
      borderColor: css("--hairline-strong"),
      borderWidth: 1,
      textStyle: { color: p.ink, fontSize: 12 },
      formatter: (params: unknown) => {
        const item = params as { dataIndex: number };
        const step = steps[item.dataIndex];
        if (!step) return "";
        return `${step.label}<br/><b>${valueFormatter(Math.abs(step.value))}</b>`;
      },
    },
    xAxis: {
      type: "category",
      data: steps.map((step) => step.label),
      axisLine: { lineStyle: { color: p.hairline } },
      axisTick: { show: false },
      axisLabel: { color: p.inkMuted, fontSize: 11, fontFamily: p.mono, hideOverlap: true },
    },
    yAxis: {
      type: "value",
      splitLine: { lineStyle: { color: p.hairline } },
      axisLabel: { color: p.inkMuted, fontSize: 11, fontFamily: p.mono, formatter: valueFormatter },
    },
    series: [
      {
        type: "bar",
        stack: "most",
        silent: true,
        itemStyle: { color: "transparent" },
        data: bases,
        barMaxWidth: 34,
      },
      {
        type: "bar",
        stack: "most",
        data: visible.map((value, index) => {
          const step = steps[index]!;
          const color =
            step.kind === "total"
              ? p.inkMuted
              : step.kind === "gain"
                ? p.gain[step.tone ?? 0]
                : p.loss[Math.min(step.tone ?? 0, p.loss.length - 1)];
          return { value, itemStyle: { color, borderRadius: 3 } };
        }),
        barMaxWidth: 34,
        // Etykieta wprost na słupku: most bez liczb wymaga liczenia ze skali,
        // a to jest wykres, który ma odpowiadać na pytanie „ile dokładnie".
        label: {
          show: true,
          position: "top",
          color: p.inkSoft,
          fontFamily: p.mono,
          fontSize: 11,
          formatter: (params: { dataIndex: number }) => {
            const step = steps[params.dataIndex];
            if (!step || step.value === 0) return "";
            const sign = step.kind === "loss" ? "−" : step.kind === "gain" ? "+" : "";
            return sign + valueFormatter(Math.abs(step.value));
          },
        },
      },
    ],
  };
}
